import React, { useState } from 'react';

import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import GridViewIcon from '@mui/icons-material/GridView';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type {
  LensCardAlignment,
  LensCardHeight,
  LensCardLayout,
  LensCardSize,
  LensCardTitleSize,
  LensSection,
} from '~/types/leLens';

import { LENS_FRAME_GAP_PX, LENS_FRAME_ROW_PX } from '../../cards/cardGrid';
import { resolveMetricAlignment } from '../../cards/metricAlignment';

const CANVAS_WIDTH_PRESETS: Record<number, LensCardSize> = { 3: 'small', 4: 'medium', 6: 'large', 12: 'full' };

// Layout: the card's own presentation, one flat list of controls instead of
// two collapsed accordions. Alignment and size are segmented controls (icon
// plus text) so the current choice reads without opening a select; height and
// the placement select stay selects, since neither benefits from an icon.

// EM_DASH is a unicode escape rather than a literal character so the source
// stays plain ASCII; the rendered text is the same dash DetailsTab used before.
const EM_DASH = String.fromCharCode(8212);

const titleSizes: Array<{ value: 'default' | LensCardTitleSize; label: string }> = [
  { value: 'default', label: 'Default' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

const cardHeights: Array<{ value: LensCardHeight | ''; label: string }> = [
  { value: '', label: 'Auto' },
  { value: 'short', label: 'Short' },
  { value: 'standard', label: 'Standard' },
  { value: 'tall', label: 'Tall' },
  { value: 'xtall', label: 'X-tall' },
];

const cardSizes: Array<{ value: LensCardSize; label: string; icon: React.ReactNode }> = [
  { value: 'small', label: 'Small', icon: <GridViewIcon fontSize="small" /> },
  { value: 'medium', label: 'Medium', icon: <ViewModuleIcon fontSize="small" /> },
  { value: 'large', label: 'Large', icon: <ViewColumnIcon fontSize="small" /> },
  { value: 'full', label: 'Full', icon: <HorizontalRuleIcon fontSize="small" /> },
];

const cardSizeHint: Record<LensCardSize, string> = {
  small: `Small ${EM_DASH} four per row`,
  medium: `Medium ${EM_DASH} three per row`,
  large: `Large ${EM_DASH} two per row`,
  full: 'Full width',
};

/** A field's true default when the draft still says 'default': the same
 *  fallback LensCardView itself applies (resolveMetricAlignment(undefined)),
 *  so the segmented control never shows a choice nothing actually reads. */
function resolvedAlignment(value: 'default' | LensCardAlignment): LensCardAlignment {
  return value === 'default' ? resolveMetricAlignment(undefined) : value;
}

/** The draft this tab edits: the card's own presentation fields, none of
 *  which name a dataset column, so no field picker here reads declared
 *  columns. */
export type DetailsTabDraft = {
  title: string;
  description: string;
  alignment: 'default' | LensCardAlignment;
  titleSize: 'default' | LensCardTitleSize;
  size: LensCardSize;
  height: LensCardHeight | '';
  section: string;
  resizeWidth?: boolean;
  resizeHeight?: boolean;
};

export type DetailsTabProps = {
  draft: DetailsTabDraft;
  onChange: (next: DetailsTabDraft) => void;
  /** Sections available on this Lens, for the placement control. */
  sections: Array<LensSection>;
  busy?: boolean;
  layout?: LensCardLayout;
};

/**
 * The panel's Layout section (legacy "Card details"): title, subtitle,
 * alignment, size and height. One flowing list rather than two accordions;
 * the resize grip on the canvas stays the fast path for size and height, this
 * is for a precise starting frame.
 */
export default function DetailsTab({ busy = false, draft, onChange, sections, layout }: DetailsTabProps) {
  const [isSubtitleOpen, setIsSubtitleOpen] = useState(Boolean(draft.description));
  const update = (patch: Partial<DetailsTabDraft>) => onChange({ ...draft, ...patch });
  const alignment = resolvedAlignment(draft.alignment);

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {isSubtitleOpen ? (
        <Stack spacing={0.75}>
          <TextField
            disabled={busy}
            fullWidth
            helperText="Shown below the name. Keep it short; the full brief lives in the query."
            label="Subtitle"
            onChange={(event) => update({ description: event.target.value })}
            size="small"
            value={draft.description}
          />
          <Box>
            <Button
              disabled={busy}
              onClick={() => {
                update({ description: '' });
                setIsSubtitleOpen(false);
              }}
              size="small"
            >
              Remove subtitle
            </Button>
          </Box>
        </Stack>
      ) : (
        <Box>
          <Button disabled={busy} onClick={() => setIsSubtitleOpen(true)} size="small">
            Add a subtitle
          </Button>
        </Box>
      )}

      <Box>
        <Typography color="text.secondary" sx={{ display: 'block', mb: 0.75 }} variant="caption">
          Alignment
        </Typography>
        <ToggleButtonGroup
          disabled={busy}
          exclusive
          fullWidth
          onChange={(_event, value: LensCardAlignment | null) => {
            if (value) {
              update({ alignment: value });
            }
          }}
          size="small"
          value={alignment}
        >
          <ToggleButton sx={{ gap: 0.75, height: 40, textTransform: 'none' }} value="left">
            <FormatAlignLeftIcon fontSize="small" />
            Left
          </ToggleButton>
          <ToggleButton sx={{ gap: 0.75, height: 40, textTransform: 'none' }} value="center">
            <FormatAlignCenterIcon fontSize="small" />
            Centre
          </ToggleButton>
          <ToggleButton sx={{ gap: 0.75, height: 40, textTransform: 'none' }} value="right">
            <FormatAlignRightIcon fontSize="small" />
            Right
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography color="text.secondary" sx={{ display: 'block', mt: 0.75 }} variant="caption">
          Changes the name and subtitle only. The headline metric keeps its usual placement.
        </Typography>
      </Box>

      <FormControl disabled={busy} fullWidth size="small">
        <InputLabel id="le-lens-card-title-size">Title size</InputLabel>
        <Select
          label="Title size"
          labelId="le-lens-card-title-size"
          onChange={(event) => update({ titleSize: event.target.value })}
          value={draft.titleSize}
        >
          {titleSizes.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Typography color="text.secondary" sx={{ display: 'block', mb: 0.75 }} variant="caption">
          Width preset
        </Typography>
        <ToggleButtonGroup
          disabled={busy}
          exclusive
          fullWidth
          onChange={(_event, value: LensCardSize | null) => {
            if (value) {
              update({ size: value, resizeWidth: true });
            }
          }}
          size="small"
          value={layout ? (CANVAS_WIDTH_PRESETS[layout.w] ?? null) : draft.size}
        >
          {cardSizes.map((option) => (
            <ToggleButton key={option.value} sx={{ gap: 0.75, height: 40, textTransform: 'none' }} value={option.value}>
              {option.icon}
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography color="text.secondary" sx={{ display: 'block', mt: 0.75 }} variant="caption">
          {layout ? `${Math.round((layout.w / 12) * 100)}% of Lens width` : cardSizeHint[draft.size]}
        </Typography>
      </Box>

      <Box>
        <Typography color="text.secondary" sx={{ display: 'block', mb: 0.75 }} variant="caption">
          Height
        </Typography>
        <ToggleButtonGroup
          disabled={busy}
          exclusive
          fullWidth
          onChange={(_event, value: LensCardHeight | '' | null) => {
            if (value !== null) {
              update({ height: value, resizeHeight: true });
            }
          }}
          size="small"
          sx={{ flexWrap: 'wrap' }}
          value={draft.height}
        >
          {cardHeights.map((option) => (
            <ToggleButton key={option.value || 'auto'} sx={{ height: 40, textTransform: 'none' }} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Typography color="text.secondary" variant="caption">
        {layout ? `Current height: ${layout.h * (LENS_FRAME_ROW_PX + LENS_FRAME_GAP_PX) - LENS_FRAME_GAP_PX}px. ` : ''}
        Drag an edge to resize one direction, or use the corner for both.
      </Typography>

      <FormControl disabled={busy || sections.length === 0} fullWidth size="small">
        <InputLabel id="le-lens-card-section">Section</InputLabel>
        <Select
          label="Section"
          labelId="le-lens-card-section"
          onChange={(event) => update({ section: event.target.value })}
          value={draft.section}
        >
          <MenuItem value="">No section</MenuItem>
          {sections.map((entry) => (
            <MenuItem key={entry.id} value={entry.id}>
              {entry.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {sections.length === 0 && (
        <Typography color="text.secondary" variant="caption">
          This Lens has no sections yet. Add one under Lens settings, Layout.
        </Typography>
      )}
    </Stack>
  );
}
